import { ModalPresence } from '../components/ModalPresence';
import { AdminEmail } from './AdminEmail';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, Check, ChevronRight, CircleUserRound, FileWarning, Fingerprint, Mail, MessageSquare, Send,
  Database, FileText, Gavel, KeyRound, LockKeyhole, Play, RefreshCw, Search, Settings, Shield, ShieldAlert, Users, Wallet, X,
} from 'lucide-react';
import { api, formatBytes } from '../api';
import { useAuth } from '../auth';
import { ResolvedReport } from '../components/ReportForensics';

type AdminTab = 'email' | 'overview' | 'support' | 'users' | 'reports' | 'evidence' | 'roles' | 'audit' | 'affiliates' | 'storage' | 'settings';

interface AdminUser {
  id: string; email: string; display_name: string; status: string; plan_code: string;
  subscription_status: string; storage_used_bytes: string; storage_quota_bytes: string;
  log_used_bytes: string; log_quota_bytes: string; active_sessions: number; active_files: number;
  roles: string[]; created_at: string; last_login_at: string | null;
}

interface AdminReport {
  id: string; reference: string; kind: 'abuse' | 'dmca'; category: string; reporter_name: string;
  reporter_email: string | null; subject: string; reported_urls: string[]; status: string; created_at: string;
}

interface AdminRole {
  id: string; name: string; description: string; is_system: boolean; permissions: string[]; users: number;
}

const tabs: Array<{ id: AdminTab; label: string; icon: typeof Users; permission?: string }> = [
  { id: 'email', label: 'Email System', icon: Mail, permission: 'email.manage' },
  { id: 'overview', label: 'Command center', icon: Activity },
  { id: 'support', label: 'Support inbox', icon: Mail, permission: 'support.read' },
  { id: 'users', label: 'Users', icon: Users, permission: 'users.read' },
  { id: 'reports', label: 'Reports', icon: Gavel, permission: 'reports.read' },
  { id: 'evidence', label: 'Evidence cases', icon: LockKeyhole, permission: 'evidence.manage' },
  { id: 'roles', label: 'Roles', icon: KeyRound, permission: 'roles.manage' },
  { id: 'audit', label: 'Audit trail', icon: Fingerprint, permission: 'audit.read' },
  { id: 'affiliates', label: 'Affiliates', icon: Wallet, permission: 'affiliates.manage' },
  { id: 'storage', label: 'Storage auditor', icon: Database },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function hasPermission(permissions: string[] | undefined, permission?: string) {
  return !permission || permissions?.includes('*') || permissions?.includes(permission);
}

export function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState<AdminTab>('overview');
  const allowedTabs = tabs.filter((item) => hasPermission(user?.admin_permissions, item.permission));
  return (
    <div className="admin-page">
      <aside className="admin-rail">
        <div><Shield /><span><strong>Administration</strong><small>Control plane</small></span></div>
        <nav>{allowedTabs.map((item) => <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}><item.icon />{item.label}</button>)}</nav>
        <p>Every administrative change is written to the audit trail.</p>
      </aside>
      <main className="admin-workspace">
        {tab === 'email' && <AdminEmail />}
        {tab === 'overview' && <AdminOverview />}
        {tab === 'support' && <AdminSupport />}
        {tab === 'users' && <AdminUsers />}
        {tab === 'reports' && <AdminReports />}
        {tab === 'evidence' && <AdminEvidence />}
        {tab === 'roles' && <AdminRoles />}
        {tab === 'audit' && <AdminAudit />}
        {tab === 'affiliates' && <AdminAffiliates />}
        {tab === 'storage' && <AdminStorage />}
        {tab === 'settings' && <AdminSettings />}
      </main>
    </div>
  );
}

function AdminHeading({ label, title, description }: { label: string; title: string; description: string }) {
  return <header className="admin-heading"><p>{label}</p><h1>{title}</h1><span>{description}</span></header>;
}

function AdminOverview() {
  const query = useQuery({ queryKey: ['admin-overview'], queryFn: () => api<any>('/admin/overview') });
  if (query.isLoading) return <div className="admin-loading" />;
  if (query.isError) return <div className="admin-error"><ShieldAlert /><h2>Administration is unavailable</h2><p>{(query.error as Error).message}</p></div>;
  const value = query.data.overview;
  return <>
    <AdminHeading label="Operations" title="Command center" description="Account access, safety workload, and infrastructure exposure in one view." />
    <div className="admin-metric-strip">
      <article><span>Registered users</span><strong>{value.users}</strong><small>{value.active_users} active</small></article>
      <article><span>Reports awaiting review</span><strong>{value.open_reports}</strong><small>Needs a recorded decision</small></article>
      <article><span>Active sessions</span><strong>{value.active_sessions}</strong><small>Across all accounts</small></article>
      <article><span>Stored customer data</span><strong>{formatBytes(value.storage_used_bytes)}</strong><small>Database-accounted usage</small></article>
    </div>
    <section className="admin-status-board">
      <div><h2>Safety readiness</h2><p>Sensitive evidence is never exposed from this panel. Reviewers work from metadata and hashes.</p></div>
      <dl>
        <div><dt>Evidence isolation</dt><dd className={query.data.evidence_configured ? 'ok' : 'danger'}>{query.data.evidence_configured ? 'Configured' : 'Action required'}</dd></div>
        <div><dt>Open evidence cases</dt><dd>{value.evidence_cases}</dd></div>
        <div><dt>Security incidents, 24h</dt><dd>{value.incidents_24h}</dd></div>
        <div><dt>Audit logging</dt><dd className="ok">Active</dd></div>
      </dl>
    </section>
  </>;
}

function AdminUsers() {
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planDuration, setPlanDuration] = useState('permanent');
  const [customPlanDays, setCustomPlanDays] = useState('');
  const durationDays = planDuration === 'permanent' ? null : Number(planDuration === 'custom' ? customPlanDays : planDuration);
  const validDuration = durationDays === null || (Number.isInteger(durationDays) && durationDays >= 1 && durationDays <= 3650);
  const [selected, setSelected] = useState<string>();
  const users = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => api<{ users: AdminUser[] }>(`/admin/users?limit=50&search=${encodeURIComponent(search)}`),
  });
  const detail = useQuery({
    queryKey: ['admin-user', selected],
    enabled: Boolean(selected),
    queryFn: () => api<any>(`/admin/users/${selected}`),
  });
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: () => api<{ roles: AdminRole[] }>('/admin/roles'), retry: false });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: () => api<{ plans: Array<{ code: string; name: string }> }>('/admin/plans') });
  const update = useMutation({
    mutationFn: (body: Record<string, any>) => api(`/admin/users/${selected}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => { setSelectedPlan(''); void client.invalidateQueries({ queryKey: ['admin-users'] }); void client.invalidateQueries({ queryKey: ['admin-user', selected] }); },
  });
  const revoke = useMutation({ mutationFn: () => api(`/admin/users/${selected}/revoke-sessions`, { method: 'POST' }), onSuccess: () => void detail.refetch() });
  const saveRoles = useMutation({
    mutationFn: (roleIds: string[]) => api(`/admin/users/${selected}/roles`, { method: 'PUT', body: JSON.stringify({ role_ids: roleIds }) }),
    onSuccess: () => { void detail.refetch(); void client.invalidateQueries({ queryKey: ['admin-users'] }); },
  });
  const assigned = detail.data?.roles?.map((role: AdminRole) => role.id) ?? [];
  return <>
    <AdminHeading label="Accounts" title="Users" description="Inspect account state, active access, capacity, billing identifiers, and enforcement history." />
    <div className="admin-toolbar"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search email, name, or user ID" /></label><span>{users.data?.users.length ?? 0} results</span></div>
    <div className="admin-master-detail">
      <div className="admin-list admin-user-list">
        {users.isLoading ? <div className="admin-loading" /> : users.data?.users.map((account) => <button key={account.id} className={selected === account.id ? 'active' : ''} onClick={() => { setSelected(account.id); setSelectedPlan(''); setPlanDuration('permanent'); setCustomPlanDays(''); update.reset(); }}>
          <span className="admin-avatar">{account.display_name.slice(0, 1).toUpperCase()}</span>
          <span><strong>{account.display_name}</strong><small>{account.email}</small></span>
          <span className={`admin-state admin-state--${account.status}`}>{account.status}</span><ChevronRight />
        </button>)}
      </div>
      <div className="admin-detail">
        {!selected ? <div className="admin-detail-empty"><CircleUserRound /><h2>Choose an account</h2><p>The complete account record opens here without leaving the list.</p></div> : detail.isLoading ? <div className="admin-loading" /> : detail.data ? <>
          <header><div><span>{detail.data.user.id}</span><h2>{detail.data.user.display_name}</h2><p>{detail.data.user.email}</p></div><span className={`admin-state admin-state--${detail.data.user.status}`}>{detail.data.user.status}</span></header>
          <div className="admin-detail-grid"><div><span>Media</span><strong>{formatBytes(detail.data.user.storage_used_bytes)}</strong><small>of {formatBytes(detail.data.user.storage_quota_bytes)}</small></div><div><span>Logs</span><strong>{formatBytes(detail.data.user.log_used_bytes)}</strong><small>of {formatBytes(detail.data.user.log_quota_bytes)}</small></div><div><span>Subscription</span><strong>{detail.data.user.plan_code}</strong><small>{detail.data.user.subscription_status}{detail.data.user.subscription_period_end ? ' · Ends ' + new Intl.DateTimeFormat('en', { dateStyle: 'short' }).format(new Date(detail.data.user.subscription_period_end)) : ' · Lifetime'}</small></div></div>
          <section><h3>Account controls</h3><div className="admin-control-row"><label><span>Status</span><select value={detail.data.user.status} onChange={(event) => update.mutate({ status: event.target.value })}><option value="active">Active</option><option value="pending_verification">Pending verification</option><option value="suspended">Suspended</option><option value="banned">Banned</option><option value="pending_deletion">Pending deletion</option></select></label><label><span>Plan</span><select value={selectedPlan || detail.data.user.plan_code} onChange={(event) => setSelectedPlan(event.target.value)}>{plans.data?.plans.map((plan) => <option key={plan.code} value={plan.code}>{plan.name}</option>)}</select></label><label><span>Duration</span><select value={planDuration} onChange={(event) => setPlanDuration(event.target.value)}><option value="permanent">Permanent / Lifetime</option><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days (1 month)</option><option value="90">90 days (3 months)</option><option value="365">365 days (1 year)</option><option value="custom">Custom days</option></select></label>{planDuration === 'custom' && <label><span>Number of days</span><input type="number" inputMode="numeric" min={1} max={3650} step={1} required value={customPlanDays} onChange={event => setCustomPlanDays(event.target.value)} placeholder="e.g. 45" aria-describedby="custom-plan-days-help" /><small id="custom-plan-days-help">1–3,650 days from now.</small></label>}<button className="button button--primary" disabled={!validDuration || update.isPending} onClick={() => { if (!validDuration) return; const code = selectedPlan || detail.data.user.plan_code; update.mutate({ plan_code: code, duration_days: durationDays }); }}>{update.isPending ? 'Applying…' : 'Apply plan'}</button><button className="button button--ghost" onClick={() => revoke.mutate()}>Revoke sessions</button></div>{update.error && <p className="form-error" role="alert">{update.error.message}</p>}</section>
          {roles.data && <section><h3>Administrative roles</h3><div className="admin-role-picker">{roles.data.roles.map((role) => <label key={role.id}><input type="checkbox" checked={assigned.includes(role.id)} onChange={(event) => saveRoles.mutate(event.target.checked ? [...assigned, role.id] : assigned.filter((id: string) => id !== role.id))} /><span><strong>{role.name}</strong><small>{role.description}</small></span></label>)}</div></section>}
          <section><h3>Sessions</h3><div className="admin-compact-list">{detail.data.sessions.map((session: any) => <div key={session.id}><span><strong>{session.ip || 'Unknown IP'}</strong><small>{session.user_agent || 'Unknown client'}</small></span><time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(session.last_seen_at))}</time><span className={`admin-state ${session.revoked_at ? 'admin-state--banned' : 'admin-state--active'}`}>{session.revoked_at ? 'revoked' : 'active'}</span></div>)}</div></section>
          <section><h3>Security incidents</h3><div className="admin-compact-list">{detail.data.incidents.length ? detail.data.incidents.map((incident: any) => <div key={incident.id}><span><strong>{incident.kind}</strong><small>{incident.id}</small></span><time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(incident.created_at))}</time><span className="admin-state admin-state--banned">{incident.severity}</span></div>) : <p className="admin-muted">No recorded incidents.</p>}</div></section>
        </> : null}
      </div>
    </div>
  </>;
}

const reportActions = [
  ['remove_content', 'Remove content'], ['warn_user', 'Warn user'], ['suspend_user', 'Suspend user'],
  ['ban_user', 'Ban user'], ['block_hash', 'Block file hash'], ['preserve_evidence', 'Preserve encrypted evidence'],
  ['csam_escalation', 'CSAM escalation'],
] as const;

function AdminReports() {
  const { user } = useAuth();
  const client = useQueryClient();
  const [selected, setSelected] = useState<string>();
  const [status, setStatus] = useState('pending');
  const reports = useQuery({ queryKey: ['admin-reports', status], queryFn: () => api<{ reports: AdminReport[] }>(`/admin/reports?limit=50&status=${status}`) });
  const detail = useQuery({ queryKey: ['admin-report', selected], enabled: Boolean(selected), queryFn: () => api<any>(`/admin/reports/${selected}`) });
  const resolve = useMutation({
    mutationFn: (body: any) => api(`/admin/reports/${selected}/resolve`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['admin-report', selected] }); void client.invalidateQueries({ queryKey: ['admin-reports'] }); },
  });
  return <>
    <AdminHeading label="Trust and safety" title="Reports" description="Review abuse and copyright notices, match objects, and apply explicit, audited actions." />
    <div className="admin-toolbar"><div className="admin-segmented">{['pending','reviewing','accepted','denied'].map((value) => <button key={value} className={status === value ? 'active' : ''} onClick={() => setStatus(value)}>{value}</button>)}</div><span>{reports.data?.reports.length ?? 0} cases</span></div>
    <div className="admin-master-detail">
      <div className="admin-list admin-report-list">{reports.isLoading ? <div className="admin-loading" /> : reports.data?.reports.map((report) => <button key={report.id} className={selected === report.id ? 'active' : ''} onClick={() => setSelected(report.id)}><span className={report.kind === 'dmca' ? 'report-kind report-kind--dmca' : 'report-kind'}>{report.kind}</span><span><strong>{report.subject}</strong><small>{report.reference} · {report.category}</small></span><time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(report.created_at))}</time><ChevronRight /></button>)}</div>
      <div className="admin-detail admin-report-detail">{!selected ? <div className="admin-detail-empty"><FileWarning /><h2>Open a report</h2><p>Review the notice, matched objects, and prior evidence records before deciding.</p></div> : detail.isLoading ? <div className="admin-loading" role="status" aria-label="Loading report" /> : detail.error ? <p role="alert" className="form-error">{detail.error.message}</p> : detail.data ? ['accepted', 'denied'].includes(detail.data.report.status) ? <ResolvedReport key={selected} detail={detail.data} canReadEvidence={Boolean(hasPermission(user?.admin_permissions, 'evidence.manage'))} /> : <ReportResolution key={selected} detail={detail.data} pending={resolve.isPending} error={resolve.error as Error | null} submit={(body) => resolve.mutate(body)} /> : null}</div>
    </div>
  </>;
}

function ReportResolution({ detail, pending, error, submit }: { detail: any; pending: boolean; error: Error | null; submit: (body: any) => void }) {
  const [fileId, setFileId] = useState(detail.matched_files[0]?.id ?? '');
  const [actions, setActions] = useState<string[]>([]);
  const report = detail.report;
  const resolveReport = (element: HTMLFormElement, decision: 'accepted' | 'denied') => {
    const form = new FormData(element);
    submit({ decision, notes: form.get('notes'), file_id: fileId || undefined, actions: decision === 'accepted' ? actions : [] });
  };
  return <form onSubmit={(event) => { event.preventDefault(); resolveReport(event.currentTarget, 'accepted'); }}>
    <header><div><span>{report.reference}</span><h2>{report.subject}</h2><p>{report.kind.toUpperCase()} from {report.reporter_name}{report.reporter_email ? ` (${report.reporter_email})` : ' · automated service'}</p></div><span className={`admin-state admin-state--${report.status}`}>{report.status}</span></header>
    <section><h3>Notice</h3><p className="report-description">{report.description}</p><div className="reported-url-list">{report.reported_urls.map((url: string) => <a key={url} href={url} target="_blank" rel="noreferrer">{url}</a>)}</div>{report.kind === 'dmca' && <dl className="dmca-facts"><div><dt>Copyrighted work</dt><dd>{report.copyrighted_work}</dd></div><div><dt>Signed by</dt><dd>{report.signature}</dd></div><div><dt>Declarations</dt><dd>{report.good_faith && report.accuracy_statement ? 'Confirmed' : 'Incomplete'}</dd></div></dl>}</section>
    <section><h3>Matched BCKT objects</h3>{detail.matched_files.length ? <div className="matched-files">{detail.matched_files.map((file: any) => <label key={file.id} className={fileId === file.id ? 'active' : ''}><input type="radio" name="file" value={file.id} checked={fileId === file.id} onChange={() => setFileId(file.id)} /><span><strong>{file.original_name}</strong><small>{file.email} · {formatBytes(file.size_bytes)} · {file.sha256}</small></span></label>)}</div> : <p className="admin-muted">No current object matched the submitted URLs. Resolve without enforcement or investigate manually.</p>}</section>
    <section><h3>Enforcement actions</h3><div className="report-actions">{reportActions.map(([value,label]) => <label key={value} className={value === 'csam_escalation' ? 'critical' : ''}><input type="checkbox" checked={actions.includes(value)} disabled={!fileId} onChange={(event) => setActions(event.target.checked ? [...actions, value] : actions.filter((item) => item !== value))} /><span>{value === 'csam_escalation' ? <ShieldAlert /> : <Check />}{label}</span></label>)}</div><p className="admin-safety-note"><LockKeyhole />CSAM escalation preserves an encrypted copy in the isolated evidence store, blacklists the hash, revokes all access, records technical identifiers, removes public delivery, and creates a case for legal reporting. The evidence itself is never rendered here.</p></section>
    <label className="admin-notes"><span>Decision notes</span><textarea name="notes" minLength={3} maxLength={10000} required placeholder="Record the evidence reviewed and the reason for this decision." /></label>
    {error && <p className="form-error">{error.message}</p>}
    <footer><button type="button" className="button button--ghost" disabled={pending} onClick={(event) => resolveReport(event.currentTarget.form!, 'denied')}>Deny report</button><button className="button button--light" disabled={pending || (actions.length > 0 && !fileId)}>{pending ? 'Applying decision…' : 'Accept and apply'}</button></footer>
  </form>;
}

function AdminEvidence() {
  const client = useQueryClient();
  const [referenceFor, setReferenceFor] = useState<string>();
  const cases = useQuery({ queryKey: ['admin-evidence'], queryFn: () => api<any>('/admin/evidence?limit=100') });
  const preserve = useMutation({
    mutationFn: (id: string) => api(`/admin/evidence/${id}/preserve`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-evidence'] }),
  });
  const markReported = useMutation({
    mutationFn: ({ id, reference }: { id: string; reference: string }) => api(`/admin/evidence/${id}/mark-reported`, { method: 'POST', body: JSON.stringify({ legal_report_reference: reference }) }),
    onSuccess: () => { setReferenceFor(undefined); void client.invalidateQueries({ queryKey: ['admin-evidence'] }); },
  });
  return <>
    <AdminHeading label="Restricted operations" title="Evidence cases" description="Encrypted preservation records and legal handoff status. No content preview or download is exposed." />
    {!cases.isLoading && !cases.data?.evidence_configured && <div className="admin-evidence-warning"><AlertTriangle /><div><strong>Evidence storage is not configured</strong><p>Pending cases remain on legal hold and cannot be removed by maintenance, but they must be preserved into the isolated encrypted store.</p></div></div>}
    <div className="evidence-table"><div className="evidence-table__head"><span>Case</span><span>Subject</span><span>Hash</span><span>Status</span><span>Legal report</span><span>Action</span></div>
      {cases.isLoading ? <div className="admin-loading" /> : cases.data?.cases.map((record: any) => <div key={record.id}>
        <span><strong>{record.report_reference || record.incident_id}</strong><small>{record.original_name || record.file_id}</small></span>
        <span><strong>{record.subject_email || 'Removed account'}</strong><small>{record.subject_user_id}</small></span>
        <code title={record.original_sha256}>{record.original_sha256?.slice(0, 14)}…</code>
        <span className={`admin-state admin-state--${record.status === 'preservation_pending' ? 'banned' : record.status === 'reported' ? 'active' : 'reviewing'}`}>{record.status}</span>
        <span>{record.legal_report_reference || 'Not recorded'}<small>{record.legal_reported_at ? new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(record.legal_reported_at)) : ''}</small></span>
        <span className="evidence-actions">{record.status === 'preservation_pending' && <button onClick={() => preserve.mutate(record.id)}>Retry preservation</button>}{record.status === 'preserved' && <button onClick={() => setReferenceFor(record.id)}>Record legal report</button>}</span>
        {referenceFor === record.id && <form className="evidence-handoff-form" onSubmit={(event) => { event.preventDefault(); const data = new FormData(event.currentTarget); markReported.mutate({ id: record.id, reference: String(data.get('reference')) }); }}><label><span>Authority or CyberTip reference</span><input name="reference" minLength={3} maxLength={500} required autoFocus /></label><button className="button button--dash-primary" disabled={markReported.isPending}>{markReported.isPending ? 'Saving…' : 'Save handoff'}</button><button type="button" className="button button--dash-quiet" disabled={markReported.isPending} onClick={() => setReferenceFor(undefined)}>Cancel</button>{markReported.error && <p role="alert" className="form-error">{markReported.error.message}</p>}</form>}
      </div>)}
    </div>
  </>;
}

function AdminRoles() {
  const client = useQueryClient();
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: () => api<{ roles: AdminRole[]; available_permissions: string[] }>('/admin/roles') });
  const create = useMutation({ mutationFn: (body: any) => api('/admin/roles', { method: 'POST', body: JSON.stringify(body) }), onSuccess: () => { void client.invalidateQueries({ queryKey: ['admin-roles'] }); } });
  const remove = useMutation({ mutationFn: (id: string) => api(`/admin/roles/${id}`, { method: 'DELETE' }), onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-roles'] }) });
  const [permissions, setPermissions] = useState<string[]>(['admin.access']);
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const form = new FormData(event.currentTarget); create.mutate({ name: form.get('name'), description: form.get('description'), permissions }); event.currentTarget.reset(); setPermissions(['admin.access']); };
  return <><AdminHeading label="Access control" title="Roles" description="Compose permissions around a job. Nobody receives sensitive evidence access by accident." />
    <div className="role-layout"><section className="role-directory">{roles.data?.roles.map((role) => <article key={role.id}><header><span><Shield /><strong>{role.name}</strong></span><small>{role.users} users</small></header><p>{role.description}</p><div>{role.permissions.map((permission) => <code key={permission}>{permission}</code>)}</div>{!role.is_system && <button onClick={() => { if (window.confirm(`Delete the role ${role.name}?`)) remove.mutate(role.id); }}><X />Delete role</button>}</article>)}</section>
      <form className="role-editor" onSubmit={submit}><h2>Create a role</h2><p>Start with the minimum access needed. Permissions take effect on the next request.</p><label><span>Name</span><input name="name" minLength={2} maxLength={80} required /></label><label><span>Description</span><textarea name="description" maxLength={500} /></label><fieldset><legend>Permissions</legend>{roles.data?.available_permissions.map((permission) => <label key={permission}><input type="checkbox" checked={permissions.includes(permission)} onChange={(event) => setPermissions(event.target.checked ? [...permissions, permission] : permissions.filter((item) => item !== permission))} /><span><strong>{permission}</strong></span></label>)}</fieldset><button className="button button--light">Create role</button></form>
    </div></>;
}

function AdminAudit() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: ['admin-audit', search], queryFn: () => api<any>(`/admin/audit?limit=100&search=${encodeURIComponent(search)}`) });
  return <><AdminHeading label="Accountability" title="Audit trail" description="An immutable timeline of role changes, enforcement decisions, plan overrides, and session revocations." /><div className="admin-toolbar"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search action, target, or administrator" /></label><span>{query.data?.pagination.total ?? 0} records</span></div><div className="audit-table"><div className="audit-table__head"><span>Time</span><span>Actor</span><span>Action</span><span>Target</span><span>IP</span></div>{query.isLoading ? <div className="admin-loading" /> : query.data?.audit.map((record: any) => <div key={record.id}><time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(record.created_at))}</time><span><strong>{record.actor_name || 'System'}</strong><small>{record.actor_email}</small></span><code>{record.action}</code><span>{record.target_type}<small>{record.target_id}</small></span><span>{record.ip || 'Unknown'}</span></div>)}</div></>;
}



function money(cents: number, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

function AdminAffiliates() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-affiliates'],
    queryFn: () => api<any>('/admin/affiliates'),
  });
  const grant = useMutation({
    mutationFn: (body: { email: string; slug?: string | undefined }) =>
      api('/admin/affiliates', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-affiliates'] }),
  });
  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: 'active' | 'suspended' }) =>
      api(`/admin/affiliates/${input.id}/status`, { method: 'POST', body: JSON.stringify({ status: input.status }) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-affiliates'] }),
  });
  const settle = useMutation({
    mutationFn: (input: { id: string; decision: 'paid' | 'rejected' }) =>
      api(`/admin/affiliates/payouts/${input.id}`, { method: 'POST', body: JSON.stringify({ decision: input.decision }) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-affiliates'] }),
  });
  const [rejectingApp, setRejectingApp] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvingApp, setApprovingApp] = useState<any | null>(null);
  const [approvalTerms, setApprovalTerms] = useState({ discount_percent: 10, discount_duration_days: '30', commission_percent: 10, commission_duration_days: '', trial_plan_code: 'free', lifetime_min_active_subscribers: '10' });

  const decideApplication = useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'reject'; rejection_reason?: string | undefined; slug?: string | undefined; discount_percent?: number; discount_duration_days?: number | null; commission_percent?: number; commission_duration_days?: number | null; trial_plan_code?: string; lifetime_min_active_subscribers?: number | null }) =>
      api(`/admin/affiliates/applications/${input.id}/decision`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin-affiliates'] });
      setRejectingApp(null);
      setApprovingApp(null);
      setRejectReason('');
    },
  });
  return <>
    <AdminHeading label="Creators" title="Affiliates" description="Grant vanity links by email, review payout requests, and suspend abuse without unlinking existing buyers." />
    <form className="admin-control-row" onSubmit={(event) => {
      event.preventDefault();
      const form = new FormData(event.currentTarget);
      grant.mutate({ email: String(form.get('email') ?? ''), slug: String(form.get('slug') ?? '') || undefined });
      event.currentTarget.reset();
    }}>
      <label><span>Email</span><input maxLength={320} name="email" type="email" required placeholder="creator@example.com" /></label>
      <label><span>Referral ID, optional</span><input name="slug" placeholder="alberteinstein" /></label>
      <button className="button button--dash-quiet" disabled={grant.isPending}>{grant.isPending ? 'Granting…' : 'Grant access'}</button>
    </form>
    {grant.error && <p className="form-error">{(grant.error as Error).message}</p>}

    <section className="admin-status-board">
      <div>
        <h2>Partner applications</h2>
        <p>Review creators and communities applying for vanity referral links. Approving grants their slug immediately.</p>
      </div>
      <div className="audit-table">
        <div className="audit-table__head">
          <span>Applicant</span>
          <span>Platform & Plan</span>
          <span>Requested slug</span>
          <span>Status</span>
          <span />
        </div>
        {query.data?.applications?.length === 0 && (
          <div style={{ padding: 'var(--space-md)', color: 'var(--color-muted)' }}>
            No partner applications yet.
          </div>
        )}
        {query.data?.applications?.map((row: any) => (
          <div key={row.id}>
            <span>
              <strong>{row.display_name}</strong>
              <small>{row.email} {row.contact_info ? '· ' + row.contact_info : ''}</small>
            </span>
            <span>
              <a href={row.platform_url.startsWith('http') ? row.platform_url : 'https://' + row.platform_url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary, #60a5fa)', display: 'inline-block', marginBottom: '2px' }}>
                {row.platform_url}
              </a>
              <small style={{ display: 'block', color: 'var(--color-muted)', whiteSpace: 'normal', maxWidth: '380px' }}>
                {row.promotion_plan}
              </small>
            </span>
            <code>bckt.io/r/{row.requested_slug}</code>
            <div>
              <span className={`admin-state admin-state--${row.status === 'approved' ? 'active' : row.status === 'rejected' ? 'banned' : 'neutral'}`}>
                {row.status}
              </span>
              {row.rejection_reason && (
                <small style={{ display: 'block', fontSize: '11px', color: 'var(--color-muted)', marginTop: '4px', maxWidth: '240px', overflowWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}>
                  {row.rejection_reason}
                </small>
              )}
            </div>
            {row.status === 'pending' ? (
              <span className="evidence-actions">
                <button
                  className="button button--dash-primary"
                  disabled={decideApplication.isPending}
                  onClick={() => { setApprovingApp(row); setApprovalTerms({ discount_percent: 10, discount_duration_days: '30', commission_percent: 10, commission_duration_days: '', trial_plan_code: 'free', lifetime_min_active_subscribers: '10' }); }}
                >
                  Approve
                </button>
                <button
                  className="button button--dash-quiet"
                  disabled={decideApplication.isPending}
                  onClick={() => {
                    setRejectingApp(row);
                    setRejectReason('');
                  }}
                >
                  Reject
                </button>
              </span>
            ) : (
              <small style={{ color: 'var(--color-muted)' }}>
                {row.reviewed_at ? new Intl.DateTimeFormat('en', { dateStyle: 'short' }).format(new Date(row.reviewed_at)) : '—'}
              </small>
            )}
          </div>
        ))}
      </div>
    </section>

    <ModalPresence>{rejectingApp && (
      <div className="secret-modal" role="dialog" aria-modal="true">
        <button className="secret-modal__scrim" onClick={() => setRejectingApp(null)} />
        <div className="purge-dialog">
          <h2>Reject partner application</h2>
          <p>
            Reject application for <strong>{rejectingApp.display_name}</strong> ({rejectingApp.email}). They will be able to reapply after a 7-day cooldown.
          </p>
          <div className="purge-options">
            <label>
              Rejection reason (optional feedback shown to applicant)
              <input
                type="text"
                placeholder="e.g. Needs higher audience volume or clearer integration"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </label>
          </div>
          <div className="dialog-actions">
            <button className="button button--dash-quiet" onClick={() => setRejectingApp(null)}>Cancel</button>
            <button
              className="button button--dash-danger"
              disabled={decideApplication.isPending}
              onClick={() => {
                decideApplication.mutate({
                  id: rejectingApp.id,
                  decision: 'reject',
                  rejection_reason: rejectReason.trim() || undefined,
                });
              }}
            >
              {decideApplication.isPending ? 'Rejecting...' : 'Reject application'}
            </button>
          </div>
        </div>
      </div>
    )}</ModalPresence>

    <ModalPresence>{approvingApp && (
      <div className="secret-modal" role="dialog" aria-modal="true">
        <button className="secret-modal__scrim" onClick={() => setApprovingApp(null)} />
        <div className="purge-dialog">
          <h2>Approve affiliate terms</h2>
          <p>Choose the customer discount, affiliate commission, trial plan and lifetime threshold before approving this application.</p>
          <div className="purge-options">
            <label>Customer discount (%)<input type="number" min="0" max="100" value={approvalTerms.discount_percent} onChange={e => setApprovalTerms(v => ({ ...v, discount_percent: Number(e.target.value) }))} /></label>
            <label>Discount duration (days, blank = lifetime)<input type="number" min="1" value={approvalTerms.discount_duration_days} onChange={e => setApprovalTerms(v => ({ ...v, discount_duration_days: e.target.value }))} /></label>
            <label>Affiliate commission (%)<input type="number" min="0" max="100" value={approvalTerms.commission_percent} onChange={e => setApprovalTerms(v => ({ ...v, commission_percent: Number(e.target.value) }))} /></label>
            <label>Commission duration (days, blank = lifetime)<input type="number" min="1" value={approvalTerms.commission_duration_days} onChange={e => setApprovalTerms(v => ({ ...v, commission_duration_days: e.target.value }))} /></label>
            <label>Trial plan<select value={approvalTerms.trial_plan_code} onChange={e => setApprovalTerms(v => ({ ...v, trial_plan_code: e.target.value }))}><option value="free">Free</option><option value="community_pro">Community Pro</option><option value="starter">Starter</option><option value="pro">Pro</option><option value="titan">Titan</option><option value="monster">Monster</option></select></label>
            <label>Required active subscribers for lifetime (0 = no requirements)<input type="number" min="0" value={approvalTerms.lifetime_min_active_subscribers} onChange={e => setApprovalTerms(v => ({ ...v, lifetime_min_active_subscribers: e.target.value }))} /></label>
          </div>
          <p>{approvalTerms.lifetime_min_active_subscribers === '0' ? 'The selected plan starts immediately, lasts for life, and has no subscriber requirement.' : 'The selected plan includes a 60-day trial, then requires the active subscriber count above.'}</p>
          <div className="dialog-actions"><button className="button button--dash-quiet" onClick={() => setApprovingApp(null)}>Cancel</button><button className="button button--dash-primary" disabled={decideApplication.isPending} onClick={() => decideApplication.mutate({ id: approvingApp.id, decision: 'approve', discount_percent: approvalTerms.discount_percent, discount_duration_days: approvalTerms.discount_duration_days ? Number(approvalTerms.discount_duration_days) : null, commission_percent: approvalTerms.commission_percent, commission_duration_days: approvalTerms.commission_duration_days ? Number(approvalTerms.commission_duration_days) : null, trial_plan_code: approvalTerms.trial_plan_code, lifetime_min_active_subscribers: Number(approvalTerms.lifetime_min_active_subscribers) })}>Approve and configure</button></div>
        </div>
      </div>
    )}</ModalPresence>

    <section className="admin-status-board">
      <div><h2>Creators</h2><p>Links stay live when a slug changes. Suspended creators cannot request payouts.</p></div>
      <div className="audit-table">
        <div className="audit-table__head"><span>Creator</span><span>Link</span><span>Balance</span><span>Status</span><span /></div>
        {query.data?.creators.map((row: any) => (
          <div key={row.user_id}>
            <span><strong>{row.display_name}</strong><small>{row.email}</small></span>
            <code>{row.slug}</code>
            <span>{money(row.available_cents)} open · {row.referred} attached</span>
            <span className={`admin-state admin-state--${row.status === 'active' ? 'active' : 'banned'}`}>{row.status}</span>
            <button className="button button--quiet" onClick={() => setStatus.mutate({ id: row.user_id, status: row.status === 'active' ? 'suspended' : 'active' })}>
              {row.status === 'active' ? 'Suspend' : 'Reinstate'}
            </button>
          </div>
        ))}
      </div>
    </section>
    <section className="admin-status-board">
      <div><h2>Payout queue</h2><p>Pay the requested amount by PayPal or IBAN, then mark it sent. Rejecting returns the reserved ledger to available.</p></div>
      <div className="audit-table">
        <div className="audit-table__head"><span>When</span><span>Creator</span><span>Destination</span><span>Amount</span><span /></div>
        {query.data?.payouts.map((row: any) => {
          const dest = row.method === 'paypal' ? row.snapshot?.paypal_email : row.snapshot?.bank_iban;
          return (
            <div key={row.id}>
              <time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(row.requested_at))}</time>
              <span><strong>{row.display_name}</strong><small>{row.email} · {row.slug}</small></span>
              <span>{row.method} · {dest || 'details on file'}</span>
              <strong>{money(row.amount_cents, row.currency)} · {row.status}</strong>
              {row.status === 'requested' ? (
                <span className="evidence-actions">
                  <button onClick={() => settle.mutate({ id: row.id, decision: 'paid' })}>Mark paid</button>
                  <button onClick={() => settle.mutate({ id: row.id, decision: 'rejected' })}>Reject</button>
                </span>
              ) : <span>{row.admin_note || row.status}</span>}
            </div>
          );
        })}
      </div>
    </section>
  </>;
}

function AdminSettings() {
  const client = useQueryClient();
  const settings = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api<{ settings: { purchases_enabled: boolean } }>('/admin/settings'),
  });
  const update = useMutation({
    mutationFn: (purchases_enabled: boolean) =>
      api('/admin/settings', { method: 'PATCH', body: JSON.stringify({ purchases_enabled }) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-settings'] }),
  });
  const enabled = settings.data?.settings.purchases_enabled !== false;
  return <>
    <AdminHeading label="Control plane" title="Settings" description="Operational switches that apply immediately across checkout and plan changes." />
    <section className="admin-status-board">
      <div>
        <h2>Plan purchases</h2>
        <p>When this is off, customers cannot start checkout or change paid plans. Existing subscriptions keep renewing.</p>
      </div>
      <dl>
        <div>
          <dt>Paid plan checkout</dt>
          <dd className={enabled ? 'ok' : 'danger'}>{enabled ? 'Open' : 'Disabled'}</dd>
        </div>
      </dl>
      <div className="admin-control-row admin-settings-actions">
        <button
          className="button button--dash-quiet"
          disabled={settings.isLoading || update.isPending}
          onClick={() => update.mutate(!enabled)}
        >
          {enabled ? 'Disable plan purchases' : 'Enable plan purchases'}
        </button>
      </div>
      {update.error && <p className="form-error">{(update.error as Error).message}</p>}
    </section>
    <AdminAllowlist />
  </>;
}

function AdminAllowlist() {
  const client = useQueryClient();
  const networks = useQuery({
    queryKey: ['admin-networks'],
    queryFn: () => api<{ networks: Array<{ network: string; mask: number; note: string; created_at: string }> }>('/admin/networks'),
  });
  const add = useMutation({
    mutationFn: (body: { network: string; note: string }) =>
      api('/admin/networks', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-networks'] }),
  });
  const remove = useMutation({
    mutationFn: (network: string) =>
      api(`/admin/networks?network=${encodeURIComponent(network)}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-networks'] }),
  });
  return (
    <section className="admin-status-board">
      <div>
        <h2>Network allowlist</h2>
        <p>Offices, campus NAT and iCloud Private Relay can look like hosting. Add a CIDR here to skip the VPN/hosting block.</p>
      </div>
      <form
        className="admin-control-row"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          add.mutate({ network: String(form.get('network') ?? ''), note: String(form.get('note') ?? '') });
          event.currentTarget.reset();
        }}
      >
        <label>
          <span>Network</span>
          <input name="network" required placeholder="203.0.113.0/24" />
        </label>
        <label>
          <span>Note</span>
          <input name="note" placeholder="Office NAT" />
        </label>
        <button className="button button--dash-quiet" disabled={add.isPending}>
          {add.isPending ? 'Adding…' : 'Allowlist'}
        </button>
      </form>
      {add.error && <p className="form-error">{(add.error as Error).message}</p>}
      <div className="audit-table">
        <div className="audit-table__head"><span>Network</span><span>Note</span><span>Added</span><span /></div>
        {networks.data?.networks.map((item) => (
          <div key={`${item.network}/${item.mask}`}>
            <code>{item.network}/{item.mask}</code>
            <span>{item.note || '—'}</span>
            <time>{new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(item.created_at))}</time>
            <button className="button button--quiet" onClick={() => remove.mutate(`${item.network}/${item.mask}`)}>Remove</button>
          </div>
        ))}
      </div>
    </section>
  );
}


interface SupportTicket {
  id: string;
  ticket_number: number;
  user_id: string | null;
  customer_email: string;
  customer_name: string;
  subject: string;
  status: 'open' | 'waiting_staff' | 'waiting_customer' | 'closed';
  assigned_to: string | null;
  assigned_to_name: string | null;
  latest_message_at: string;
  created_at: string;
  message_count: number;
}

interface SupportMessage {
  id: string;
  ticket_id: string;
  sender: 'customer' | 'staff' | 'system';
  author_user_id: string | null;
  author_name: string;
  author_email: string;
  body_text: string;
  auto_replied: boolean;
  created_at: string;
}

function AdminSupport() {
  const { user } = useAuth();
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [nickname, setNickname] = useState(user?.staff_nickname || '');
  const [title, setTitle] = useState(user?.staff_signature_title || '');

  const tickets = useQuery({
    queryKey: ['admin-support-tickets', filterStatus, search],
    queryFn: () => api<{ tickets: SupportTicket[] }>(`/admin/support/tickets?status=${filterStatus}&search=${encodeURIComponent(search)}`),
    refetchInterval: 10000,
  });

  const detail = useQuery({
    queryKey: ['admin-support-ticket', selectedId],
    queryFn: () => api<{ ticket: SupportTicket & { customer_plan_code?: string; customer_storage_used?: string }; messages: SupportMessage[] }>(`/admin/support/tickets/${selectedId}`),
    enabled: Boolean(selectedId),
    refetchInterval: 5000,
  });

  const sendReply = useMutation({
    mutationFn: (message: string) =>
      api(`/admin/support/tickets/${selectedId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      }),
    onSuccess: () => {
      setReplyText('');
      void client.invalidateQueries({ queryKey: ['admin-support-ticket', selectedId] });
      void client.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });

  const updateStatus = useMutation({
    mutationFn: (status: string) =>
      api(`/admin/support/tickets/${selectedId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin-support-ticket', selectedId] });
      void client.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });

  const saveProfile = useMutation({
    mutationFn: (body: { staff_nickname: string | null; staff_signature_title: string | null }) =>
      api('/admin/support/staff-profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['auth-me'] });
      alert('Staff signature updated.');
    },
  });

  const activeTicket = detail.data?.ticket;

  return <>
    <AdminHeading
      label="Customer care"
      title="Support inbox"
      description="Inbound emails to hello@bckt.io open tickets here. Reply with your staff nickname and signatures."
    />

    <div className="admin-status-board" style={{ marginTop: '24px', paddingBottom: '24px', borderBottom: 'var(--rule-hair) solid var(--color-rule)' }}>
      <div>
        <h2>Your staff identity</h2>
        <p>This name and title are signed at the bottom of all support emails you send to customers.</p>
      </div>
      <form
        className="admin-control-row"
        onSubmit={(e) => {
          e.preventDefault();
          saveProfile.mutate({
            staff_nickname: nickname.trim() || null,
            staff_signature_title: title.trim() || null,
          });
        }}
      >
        <label>
          <span>Staff nickname / alias</span>
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder={user?.display_name || 'e.g. Thomas, Alex, Sam'}
          />
        </label>
        <label>
          <span>Role title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Lead Support, Infrastructure Ops"
          />
        </label>
        <button className="button button--dash-quiet" disabled={saveProfile.isPending}>
          {saveProfile.isPending ? 'Saving…' : 'Save profile'}
        </button>
      </form>
    </div>

    <div className="admin-toolbar">
      <div className="admin-segmented">
        {['all', 'open', 'waiting_staff', 'waiting_customer', 'closed'].map((value) => (
          <button
            key={value}
            className={filterStatus === value ? 'active' : ''}
            onClick={() => setFilterStatus(value)}
          >
            {value.replace('_', ' ')}
          </button>
        ))}
      </div>
      <label>
        <Search />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search ticket #, subject, or email..."
        />
      </label>
      <span>{tickets.data?.tickets.length ?? 0} conversations</span>
    </div>

    <div className="admin-master-detail" style={{ height: 'min(780px, calc(100dvh - 200px))' }}>
      <div className="admin-list">
        {tickets.isLoading ? (
          <div className="admin-loading" />
        ) : tickets.data?.tickets.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-muted)' }}>
            No support tickets in this view.
          </div>
        ) : (
          tickets.data?.tickets.map((t) => {
            const isUnread = t.status === 'waiting_staff';
            return (
              <button
                key={t.id}
                className={selectedId === t.id ? 'active' : ''}
                onClick={() => setSelectedId(t.id)}
                style={{
                  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                  background: isUnread && selectedId !== t.id ? 'color-mix(in oklch, var(--color-accent) 5%, transparent)' : undefined,
                }}
              >
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isUnread ? 'var(--color-accent)' : 'transparent' }} />
                <span>
                  <strong>#{t.ticket_number} · {t.subject}</strong>
                  <small>{t.customer_name || t.customer_email} · {t.message_count} msg</small>
                </span>
                <span className={`admin-state admin-state--${t.status === 'closed' ? 'banned' : t.status === 'waiting_staff' ? 'active' : 'suspended'}`}>
                  {t.status.replace('_', ' ')}
                </span>
              </button>
            );
          })
        )}
      </div>

      <div className="admin-detail" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0' }}>
        {!selectedId || !activeTicket ? (
          <div className="admin-detail-empty">
            <Mail />
            <h2>Open a support ticket</h2>
            <p>Select a ticket on the left to read messages and respond directly by email.</p>
          </div>
        ) : detail.isLoading ? (
          <div className="admin-loading" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <header style={{ padding: '20px 24px', borderBottom: 'var(--rule-hair) solid var(--color-rule)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-accent)' }}>
                  TICKET #{activeTicket.ticket_number}
                </span>
                <h2 style={{ fontSize: '20px', margin: '4px 0 2px' }}>{activeTicket.subject}</h2>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-muted)' }}>
                  From <strong>{activeTicket.customer_name}</strong> &lt;{activeTicket.customer_email}&gt;
                  {activeTicket.customer_plan_code && ` · Plan: ${activeTicket.customer_plan_code.toUpperCase()}`}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={activeTicket.status}
                  onChange={(e) => updateStatus.mutate(e.target.value)}
                  style={{ minHeight: '36px', padding: '0 12px', background: 'var(--color-paper-2)', color: 'var(--color-ink)', border: 'var(--rule-hair) solid var(--color-rule)', borderRadius: 'var(--radius-sm)' }}
                >
                  <option value="open">Open</option>
                  <option value="waiting_staff">Waiting Staff</option>
                  <option value="waiting_customer">Waiting Customer</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </header>

            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {detail.data?.messages.map((m) => {
                const isStaff = m.sender === 'staff';
                return (
                  <article
                    key={m.id}
                    style={{
                      alignSelf: isStaff ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      background: isStaff ? 'var(--color-paper-2)' : 'color-mix(in oklch, var(--color-ink) 4%, transparent)',
                      border: 'var(--rule-hair) solid var(--color-rule)',
                      borderLeft: isStaff ? '3px solid var(--color-accent)' : 'var(--rule-hair) solid var(--color-rule)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px 20px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '8px', fontSize: '12px', color: 'var(--color-muted)' }}>
                      <strong>{m.author_name} {isStaff ? '(Staff)' : ''}</strong>
                      <time>{new Intl.DateTimeFormat('en', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(m.created_at))}</time>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px' }}>
                      {m.body_text}
                    </div>
                  </article>
                );
              })}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!replyText.trim()) return;
                sendReply.mutate(replyText);
              }}
              style={{
                padding: '16px 24px',
                borderTop: 'var(--rule-hair) solid var(--color-rule)',
                background: 'var(--color-paper-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write an email reply to the customer..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-paper)',
                  color: 'var(--color-ink)',
                  border: 'var(--rule-hair) solid var(--color-rule)',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                  Will send as <strong>{user?.staff_nickname || user?.display_name || 'Staff'}</strong> from hello@bckt.io
                </span>
                <button
                  type="submit"
                  className="button button--dash-quiet"
                  disabled={sendReply.isPending || !replyText.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Send style={{ width: '14px', height: '14px' }} />
                  {sendReply.isPending ? 'Sending...' : 'Send email reply'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  </>;
}


function AdminStorage() {
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedRun, setSelectedRun] = useState<any | null>(null);

  const query = useQuery({
    queryKey: ['admin-storage-reconciliation', page],
    queryFn: () =>
      api<{
        total: number;
        page: number;
        limit: number;
        active_run: any;
        runs: Array<{
          id: string;
          status: 'running' | 'completed' | 'failed';
          trigger: 'cron' | 'manual';
          started_at: string;
          completed_at: string | null;
          objects_scanned: number;
          orphans_found: number;
          orphans_deleted: number;
          bytes_reclaimed: string;
          error_message: string | null;
          details?: {
            purged_objects?: Array<{ key: string; size_bytes: number; last_modified?: string }>;
          };
        }>;
      }>(`/admin/storage/reconciliation?page=${page}&limit=10`),
    refetchInterval: 3000,
  });

  const trigger = useMutation({
    mutationFn: () => api('/admin/storage/reconciliation/trigger', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['admin-storage-reconciliation'] }),
  });

  const activeRun = query.data?.active_run;
  const runs = query.data?.runs ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <>
      <AdminHeading
        label="Infrastructure"
        title="Storage auditor & reconciler"
        description="Scans the primary storage bucket at 1 RPS, compares against Postgres, and purges orphaned objects older than 24 hours. Scheduled every Monday at 00:00 UTC."
      />

      <section className="admin-status-board">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2>Reconciliation status</h2>
            <p>
              {activeRun
                ? 'A storage scan is currently running in background (1 RPS rate-limited).'
                : 'No active scan running. Scheduled cron runs every Monday at 00:00 UTC.'}
            </p>
          </div>
          <button
            className="button button--dash-quiet"
            disabled={Boolean(activeRun) || trigger.isPending}
            onClick={() => trigger.mutate()}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {activeRun ? (
              <>
                <RefreshCw className="spin" style={{ width: '14px', height: '14px' }} />
                Scanning in progress...
              </>
            ) : (
              <>
                <Play style={{ width: '14px', height: '14px' }} />
                Run manual scan now
              </>
            )}
          </button>
        </div>

        {activeRun && (
          <div
            style={{
              marginTop: '16px',
              padding: '16px 20px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px',
            }}
          >
            <div>
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Trigger</span>
              <strong style={{ fontSize: '13px', textTransform: 'capitalize' }}>{activeRun.trigger}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Objects scanned</span>
              <strong style={{ fontSize: '15px' }}>{activeRun.objects_scanned?.toLocaleString()}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Orphans found</span>
              <strong style={{ fontSize: '15px', color: '#e5a440' }}>{activeRun.orphans_found?.toLocaleString()}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Orphans deleted</span>
              <strong style={{ fontSize: '15px', color: '#4ade80' }}>{activeRun.orphans_deleted?.toLocaleString()}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--color-muted)', display: 'block' }}>Space reclaimed</span>
              <strong style={{ fontSize: '15px' }}>{formatBytes(activeRun.bytes_reclaimed || 0)}</strong>
            </div>
          </div>
        )}
      </section>

      <section className="admin-status-board" style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2>Scan history (Latest 100 kept)</h2>
            <p>Total recorded scans: {total}</p>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="button button--dash-quiet"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ padding: '4px 10px', fontSize: '12px' }}
              >
                Previous
              </button>
              <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="button button--dash-quiet"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ padding: '4px 10px', fontSize: '12px' }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'var(--color-muted)' }}>
                <th style={{ padding: '10px 12px' }}>Date</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Trigger</th>
                <th style={{ padding: '10px 12px' }}>Scanned</th>
                <th style={{ padding: '10px 12px' }}>Orphans</th>
                <th style={{ padding: '10px 12px' }}>Deleted</th>
                <th style={{ padding: '10px 12px' }}>Reclaimed</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Report</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: 'var(--color-muted)' }}>
                    No reconciliation runs recorded yet.
                  </td>
                </tr>
              ) : (
                runs.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      {new Date(r.started_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background:
                            r.status === 'completed'
                              ? 'rgba(74, 222, 128, 0.15)'
                              : r.status === 'running'
                              ? 'rgba(59, 130, 246, 0.15)'
                              : 'rgba(239, 68, 68, 0.15)',
                          color:
                            r.status === 'completed'
                              ? '#4ade80'
                              : r.status === 'running'
                              ? '#60a5fa'
                              : '#f87171',
                        }}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textTransform: 'capitalize' }}>{r.trigger}</td>
                    <td style={{ padding: '10px 12px' }}>{r.objects_scanned?.toLocaleString()}</td>
                    <td style={{ padding: '10px 12px', color: r.orphans_found > 0 ? '#e5a440' : 'inherit' }}>
                      {r.orphans_found?.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', color: r.orphans_deleted > 0 ? '#4ade80' : 'inherit' }}>
                      {r.orphans_deleted?.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                      {formatBytes(r.bytes_reclaimed || 0)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button
                        className="button button--dash-quiet"
                        style={{ padding: '3px 8px', fontSize: '11px' }}
                        onClick={() => setSelectedRun(r)}
                      >
                        View report
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedRun && (
        <div
          className="admin-dialog-layer"
          onClick={() => setSelectedRun(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            className="admin-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#161714',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '680px',
              width: '100%',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#f4f4f0', margin: 0 }}>
                  Reconciliation Report
                </h3>
                <small style={{ color: 'var(--color-muted)' }}>
                  {new Date(selectedRun.started_at).toLocaleString()} &middot; {selectedRun.trigger} run
                </small>
              </div>
              <button
                onClick={() => setSelectedRun(null)}
                style={{ background: 'transparent', border: 'none', color: '#94958f', cursor: 'pointer' }}
              >
                <X style={{ width: '18px', height: '18px' }} />
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '10px',
                padding: '12px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center',
              }}
            >
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>Scanned</span>
                <strong style={{ display: 'block', fontSize: '14px' }}>{selectedRun.objects_scanned}</strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>Orphans</span>
                <strong style={{ display: 'block', fontSize: '14px', color: '#e5a440' }}>{selectedRun.orphans_found}</strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>Deleted</span>
                <strong style={{ display: 'block', fontSize: '14px', color: '#4ade80' }}>{selectedRun.orphans_deleted}</strong>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>Reclaimed</span>
                <strong style={{ display: 'block', fontSize: '14px' }}>{formatBytes(selectedRun.bytes_reclaimed || 0)}</strong>
              </div>
            </div>

            {selectedRun.error_message && (
              <div
                style={{
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '6px',
                  color: '#f87171',
                  fontSize: '13px',
                  marginBottom: '16px',
                }}
              >
                <strong>Scan error:</strong> {selectedRun.error_message}
              </div>
            )}

            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#f4f4f0' }}>
              Purged Objects List ({selectedRun.details?.purged_objects?.length ?? 0})
            </h4>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '6px',
                background: '#10110e',
              }}
            >
              {!selectedRun.details?.purged_objects || selectedRun.details.purged_objects.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-muted)', fontSize: '13px' }}>
                  {selectedRun.orphans_deleted > 0
                    ? 'Scan completed before detail tracking was enabled for this run.'
                    : 'No orphan objects were found during this scan. Storage is 100% in sync.'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'var(--color-muted)' }}>
                      <th style={{ padding: '8px 12px' }}>Storage Key</th>
                      <th style={{ padding: '8px 12px' }}>Size</th>
                      <th style={{ padding: '8px 12px' }}>B2 Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRun.details.purged_objects.map((obj: any, idx: number) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#f4f4f0', wordBreak: 'break-all' }}>
                          {obj.key}
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#94958f' }}>
                          {formatBytes(obj.size_bytes)}
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#94958f' }}>
                          {obj.last_modified ? new Date(obj.last_modified).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="button button--dash-quiet" onClick={() => setSelectedRun(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
